'use server'

import { Mixed } from 'common-types'
import { getClient } from '@/lib/api/client'
import { AxiosResponse } from 'axios'

export interface ApiResponsePagination {
    page: number
    next: number
    prev: number
    pages: number[]
    first: boolean
    end: boolean
    limit: number
}

export type ApiResponse<T> = {
    success: boolean
    result: T
    error?: string
    errorCode?: string
    pagination?: ApiResponsePagination
    total?: number
}

type ResponseType<T> = T & {
    data: T[]
    total: number
    pagination: ApiResponsePagination
}

export const apiRequest = async <T = any>(
    route: string,
    config: {
        params?: Mixed
        data?: FormData | Mixed
        id?: string
        formData?: boolean
    },
    method: 'get' | 'post' | 'put' | 'delete' | 'patch' = 'get'
): Promise<ApiResponse<T>> => {
    try {
        const client = await getClient(config.formData)

        let response: AxiosResponse<ResponseType<T>, any>

        switch (method) {
            case 'get':
                response = await client.get<ResponseType<T>>(
                    '/' + route + (config?.id ? '/' + config.id : ''),
                    {
                        params: config.params,
                    }
                )
                break
            case 'post':
                response = await client.post<ResponseType<T>>(
                    '/' + route + (config?.id ? '/' + config.id : ''),
                    config.data,
                    {
                        params: config.params,
                    }
                )

                break
            case 'put':
                response = await client.put<ResponseType<T>>(
                    '/' + route + (config.id ? '/' + config.id : ''),
                    config.data,
                    {
                        params: config.params,
                    }
                )
                break
            case 'patch':
                response = await client.patch<ResponseType<T>>(
                    '/' + route + (config.id ? '/' + config.id : ''),
                    config.data,
                    {
                        params: config.params,
                    }
                )
                break
            case 'delete':
                response = await client.delete<ResponseType<T>>(
                    '/' + route + (config.id ? '/' + config.id : ''),
                    {
                        params: config.params,
                    }
                )
                break
        }

        let data: any
        let pagination: ApiResponsePagination | undefined
        let total: number | undefined

        if (response.data.pagination) {
            data = response.data.data
            pagination = response.data.pagination
            total = response.data.total
        } else {
            data = response.data
        }

        return {
            success: true,
            result: data,
            pagination,
            total,
        }
    } catch (error: any) {
        console.log('error: ', error)
        console.log('error.response.data: ', error?.response?.data)

        // Enviar erros 500+ para o Sentry
        const status = error?.response?.status
        if (status >= 500) {
            import('@sentry/react').then(Sentry => {
                Sentry.captureException(error, {
                    extra: {
                        url: route,
                        method,
                        status,
                        responseData: error?.response?.data,
                    },
                })
            }).catch(() => {})
        }

        const errorCode = error?.response?.data?.code
        const subscriptionErrorCodes = [
            'NO_SUBSCRIPTION',
            'SUBSCRIPTION_SUSPENDED',
            'PAYMENT_PENDING',
        ]

        if (subscriptionErrorCodes.includes(errorCode)) {
            // Leva à aba financeira da conta, onde a mensalidade é paga — o
            // mesmo destino do botão da tarja. Antes o destino era
            // `/conta#assinatura`, rota inexistente: o catch-all devolvia para
            // `/`, a tela refazia a escrita barrada pelo guard e recarregava de
            // novo, sem fim. Já estando na conta, não navega: uma escrita
            // barrada ali não pode recarregar a própria tela de pagamento.
            if (!window.location.pathname.startsWith('/account')) {
                window.location.href = '/account?tab=financial'
            }
            return {
                result: {} as any,
                success: false,
                error:
                    error?.response?.data?.message ||
                    'Erro relacionado à assinatura',
            }
        }

        const errors: string[] = []

        if (Array.isArray(error?.response?.data?.message)) {
            errors.push(...error?.response?.data?.message)
        }

        return {
            result: {} as any,
            success: false,
            error: errors.length
                ? errors.join(', ')
                : error?.response?.data?.message ||
                  'Ocorreu um erro ao realizar a requisição',
            errorCode: error?.response?.data?.error || error?.response?.data?.code,
        }
    }
}
